<script setup lang="ts">
import { computed } from 'vue';

interface PortraitCardOverlayItem {
  isError?: boolean;
  isGenerated?: boolean;
  isPreloaded?: boolean;
  isRetrying?: boolean;
  retryCount?: number;
  uuid: string;
}

interface Props {
  classPrefix: 'LegacyPortraitGallery' | 'PortraitImageGrid';
  // A group portrait among the singles: the card shows the group text while
  // its job runs.
  group?: boolean;
  portrait: PortraitCardOverlayItem;
  selected: boolean;
  selectedCount: number | undefined;
}

interface Emits {
  countDecrement: [event: MouseEvent];
  countIncrement: [event: MouseEvent];
  tryAgain: [];
}

const props = withDefaults(defineProps<Props>(), { group: false });
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<Emits>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const classes = computed(() => {
  const prefix = props.classPrefix;
  return {
    counter: `${prefix}__counter`,
    counterButton: `${prefix}__counter-button`,
    counterNumber: `${prefix}__counter-number`,
    error: `${prefix}__error`,
    errorRetry: `${prefix}__error-retry`,
    errorRetryCount: `${prefix}__error-retry-count`,
    noPhotoIcon: `${prefix}__no-photo-icon`,
    placeholder: `${prefix}__placeholder`,
    placeholderLabel: `${prefix}__placeholder-label`,
  };
});
</script>

<template>
  <div
    v-if="portrait.isError"
    :class="classes.error"
    :data-testid="group ? 'portraits-group-error' : undefined"
  >
    {{ $t('Portraits.error') }}
    <button
      :class="[classes.errorRetry, 'kiosk-button']"
      data-testid="portraits-error-retry"
      :disabled="portrait.isRetrying || (portrait.retryCount ?? 0) >= 3"
      @click="emit('tryAgain')"
    >
      {{
        portrait.isRetrying
          ? $t('Portraits.retrying')
          : (portrait.retryCount ?? 0) >= 3
            ? $t('Portraits.maxRetriesReached')
            : $t('Portraits.buttonRetry')
      }}
    </button>
    <div
      v-if="(portrait.retryCount ?? 0) > 0"
      :class="classes.errorRetryCount"
    >
      {{
        $t('Portraits.retryAttempts', {
          count: portrait.retryCount,
        })
      }}
    </div>
  </div>

  <div v-else-if="!portrait.isPreloaded" :class="classes.placeholder">
    <div
      v-if="group"
      :class="classes.placeholderLabel"
      data-testid="portraits-group-placeholder"
    >
      {{ $t('Portraits.groupPlaceholder') }}
    </div>
    <div v-else-if="portrait.isGenerated" :class="classes.placeholderLabel">
      {{ $t('Portraits.placeholderLoading') }}
    </div>
    <div v-else :class="classes.placeholderLabel">
      {{ $t('Portraits.placeholderGenerating') }}
    </div>
  </div>

  <template v-else>
    <i :class="classes.noPhotoIcon" />
    <div v-show="selected" :class="classes.counter">
      <button
        :class="[classes.counterButton, 'kiosk-button']"
        data-testid="portrait-counter-dec"
        @click="emit('countDecrement', $event)"
      >
        &minus;
      </button>
      <span :class="classes.counterNumber" data-testid="portrait-counter-value">
        {{ selectedCount }}
      </span>
      <button
        :class="[classes.counterButton, 'kiosk-button']"
        data-testid="portrait-counter-inc"
        @click="emit('countIncrement', $event)"
      >
        +
      </button>
    </div>
  </template>
</template>

<style lang="scss" scoped>
@keyframes PortraitCardOverlay__placeholder {
  0% {
    background-position: 0 -100%;
  }

  100% {
    background-position: 0 200%;
  }
}

:is(
  .LegacyPortraitGallery__placeholder,
  .PortraitImageGrid__placeholder
) {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20%;
  font-size: 1.2rem;
  color: var(--Kiosk--tint-inverse-color);
  text-align: center;
  text-transform: var(--Kiosk--font_capitalize, uppercase);
  background: rgba(var(--Kiosk--tint-color-rgb), 0.15);
  border-radius: var(--ImageGallery--item-radius);
  box-shadow: inset 0 0 1rem 0.3rem
    rgba(var(--Kiosk--tint-color-rgb), 0.2);

  &::before {
    position: absolute;
    inset: 0;
    content: '';
    background-image: linear-gradient(
      0deg,
      transparent,
      rgba(var(--Kiosk--tint-color-rgb), 0.35),
      transparent
    );
    background-repeat: no-repeat;
    background-size: 100% calc(var(--ImageGallery--item-height) * 2);
    border-radius: var(--ImageGallery--item-radius);
    animation: PortraitCardOverlay__placeholder 3s linear infinite;
  }
}

:is(
  .LegacyPortraitGallery__placeholder-label,
  .PortraitImageGrid__placeholder-label
) {
  position: relative;
  z-index: 1;
}

:is(.LegacyPortraitGallery__counter, .PortraitImageGrid__counter) {
  position: absolute;
  bottom: var(--portrait-card-counter-bottom, 1rem);
  left: var(--portrait-card-counter-left, 1rem);
  display: flex;
  align-items: center;
  padding: var(--portrait-card-counter-padding, 0.3rem 0.4rem);
  background-color: var(--Kiosk--button_background_color);
  border-radius: 3rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

:is(
  .LegacyPortraitGallery__counter-button,
  .PortraitImageGrid__counter-button
) {
  --kiosk-button-width: var(--portrait-card-counter-button-size, 2.5rem);
  --kiosk-button-min-width: var(--portrait-card-counter-button-size, 2.5rem);
  --kiosk-button-height: var(--portrait-card-counter-button-size, 2.5rem);
  --kiosk-button-min-height: var(--portrait-card-counter-button-size, 2.5rem);
  --kiosk-button-padding: 0;
  --kiosk-button-font-size: var(
    --portrait-card-counter-button-font-size,
    2rem
  );
  --kiosk-button-color: var(--Kiosk--button_background_color);
  --kiosk-button-background: var(--Kiosk--button_font_color);
  --kiosk-button-border-radius: 100%;

  line-height: 1;
}

:is(
  .LegacyPortraitGallery__counter-number,
  .PortraitImageGrid__counter-number
) {
  padding: var(--portrait-card-counter-number-padding, 0 0.6rem);
  font-size: var(--portrait-card-counter-number-font-size, 1.6rem);
  font-variation-settings: 'wght' 450;
  color: var(--Kiosk--button_font_color);
}

:is(
  .LegacyPortraitGallery__no-photo-icon,
  .PortraitImageGrid__no-photo-icon
) {
  position: absolute;
  right: calc(var(--ImageGallery--item-width) * 0.05);
  bottom: calc(var(--ImageGallery--item-width) * 0.055);
  z-index: 5;
  width: calc(
    var(--ImageGallery--item-width) * 0.6 *
      var(--Kiosk--portrait_no_photo_icon_size, 30) / 100
  );
  height: calc(
    var(--ImageGallery--item-width) * 0.6 *
      var(--Kiosk--portrait_no_photo_icon_size, 30) / 100
  );
  pointer-events: auto;

  &::after {
    position: absolute;
    inset: 0;
    content: '';
    background-color: #dc2726;
    mask-image: url('../../../assets/images/gallery-no-photo.svg');
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
  }
}

:is(.LegacyPortraitGallery__error, .PortraitImageGrid__error) {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10%;
  font-size: var(--ImageGallery--text-size);
  color: var(--Kiosk--tint-inverse-color);
  text-align: center;
  text-transform: var(--Kiosk--font_capitalize, uppercase);
  background: rgba(var(--Kiosk--tint-color-rgb), 0.15);
  border-radius: var(--ImageGallery--item-radius);
  box-shadow: inset 0 0 1rem 0.3rem
    rgba(var(--Kiosk--tint-color-rgb), 0.2);
}

:is(
  .LegacyPortraitGallery__error-retry,
  .PortraitImageGrid__error-retry
) {
  margin-top: var(--ImageGallery--text-size);
  font-size: var(--portrait-generation-error-retry-font-size);
}

:is(
  .LegacyPortraitGallery__error-retry-count,
  .PortraitImageGrid__error-retry-count
) {
  margin-top: calc(var(--ImageGallery--text-size) * 0.5);
  font-size: calc(var(--ImageGallery--text-size) * 0.65);
  opacity: 0.8;
}
</style>
